/**
 * **Status-keyed action legality** — the ONE status-keyed legality wall: given an actor (and, for
 * benefit-side rules, a target), reject an illegal intent PRE-DICE with a rules-cited message —
 * the Hidden-gate shape (hard reject, GM `force_*` punches through) applied to Jammed / Stunned /
 * Down-and-Out / Slowed / Grappled instead of targeting/tech-immunity. Three consumers below, all
 * sharing the same status reads:
 *   - `checkActorStatusLegality` — the actor-side wall for a standard (non-reaction) verb:
 *     Stunned/Down-and-Out's can't-act (all actions, excepted by pinned action id), Jammed's
 *     legal-attack-set + tech ban. Wired at the room's generic dispatch seam (beside
 *     `checkActionUseLegality`), the domain reducer's `boost` case (Boost self-enforces there),
 *     AND inside each of the pre-ladder self-gating referees' own gates
 *     (`refereeSelfAction.ts`'s Hide/Disengage/Shut Down/Boot Up/Engage COMP/CON/Self-Destruct;
 *     `refereeContestAction.ts`'s Search/Grapple-break/Grapple-release gameplay leg;
 *     `refereeDeployAction.ts`'s all five drone/mine verbs; `refereeIdentityAction.ts`'s all six
 *     pilot↔mech identity verbs — Mount/Jockey/Jockey Menu/Ride/Ride-Dismount keyed on the
 *     dispatching PILOT, Shake Off keyed on the dispatching MECH). Those verbs dispatch BEFORE
 *     the room's shared gate ladder ever runs, so the generic dispatch-seam call never reaches
 *     them and each rings this wall itself, first, in its own gate.
 *     `refereeContestAction.ts`'s Grapple-contest arm deliberately gets NO ring (RAW p.69: the
 *     same-Size turn-start contest isn't an action, so p.78's action ban never reaches it — see
 *     that file's own comment). A GM **`gm_force`** never reaches this wall: the room routes it
 *     to `refereeForce` ahead of every gate (`decisions/0209`).
 *   - `checkReactionStatusLegality` — the actor-side wall for a REACTION declaration (Overwatch,
 *     Brace, any authored/protector reaction): Jammed and Grappled forbid all reactions
 *     outright, Stunned/Down-and-Out's "no actions or reactions" reaches the same conclusion, and
 *     Braced carries Brace's own "no reactions until the end of your next turn" (RAW p.73).
 *     Consumed by the room's Overwatch block AND `reactionRegistry.ts`'s `reactionAvailable` (so
 *     Brace/content reactions share the identical wall, no second mechanism).
 *   - `checkJammedBenefitLegality` — the TARGET-side wall: a beneficial tech action (Bolster)
 *     can't reach a Jammed target, even from an ally (RAW p.78).
 *
 * **This is its own leaf module, not folded into `economy.ts`, on purpose**: `reactionRegistry.ts`
 * (which `resolve/damageApply.ts` imports, behind the `resolve.ts` facade that `economy.ts`'s
 * `economy/` modules import `selectWeapon` from) needs `checkReactionStatusLegality` too —
 * importing it FROM `economy.ts` would close the cycle `economy.ts → economy/cost.ts →
 * resolve.ts → resolve/damageApply.ts → reactionRegistry.ts → economy.ts`. This module imports only from
 * `@lancer-vtt/domain`/`@lancer-vtt/shared`, so every consumer (`economy.ts`, `reactionRegistry.ts`,
 * `encounterRoom.ts`, the domain reducer's `boost` case) can import it as a shared leaf with no
 * risk of a cycle.
 */

import { BRACED_STATUS, type EncounterState, hasStatus, isStunned, JAMMED_STATUS, GRAPPLED_STATUS, DOWN_AND_OUT_STATUS, RODEO_STATUS, TOWING_STATUS, REELING_STATUS, noReactionsAreaHolding, worldFor } from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";

/** The status name the Brace reaction lands; `reactionRegistry.ts` re-exports it as the public name. */
export { BRACED_STATUS };

/** A status-legality verdict — `Result<void>` (mirrors `economy.ts`'s `BudgetVerdict`, duplicated
 * here rather than imported to keep this module a dependency-free leaf; see the module doc). */
export type StatusLegalityVerdict = Result<void>;

/**
 * The status SAFE_CRACKER's Hack/Slash invade option plants (`t_hacker:2`): "Your target cannot
 * benefit from or take QUICK or FULL TECH actions until the mech is either SHUT DOWN or its core
 * computer is rebooted with a successful SYSTEMS check as a quick action." Jammed's two tech walls
 * without Jammed's attack and reaction limits.
 */
export const HACK_SLASH_STATUS = "Hack/Slash";

/**
 * Attack ids Jammed leaves legal (RAW Core p.78: "may still make melee attacks with the
 * Improvised Attack or Grapple actions, or perform a Ram") — matched against the WEAPON id an
 * attack fires (`intent.weapon`), not the derived once-per-turn action id, since a bare Barrage
 * shot naturally carries the weapon id and nothing else.
 */
const JAMMED_LEGAL_ATTACK_WEAPONS = new Set(["act_improvised_attack", "act_grapple", "act_ram"]);

/**
 * Stunned's can't-act wall excepts EXACTLY these once-per-turn action ids (`actionIdForIntent`'s
 * derivation) — `act_boot_up` because a Stunned-via-Shut-Down mech may still Boot Up;
 * `act_dismount`/`act_eject` per RAW p.78 ("pilots of Stunned mechs may still Mount, Dismount, or
 * Eject"). `refereeSelfAction.ts`'s `request_boot_up` gate rings this wall itself — since it is
 * one of the pre-ladder self-gating referees' own dedicated early intent branches (Hide/
 * Disengage/Shut Down/Boot Up/Engage COMP/CON/Self-Destruct), it never reaches this module's
 * generic-dispatch-seam call site, so it calls in directly, passing the literal `"act_boot_up"`.
 * Dismount/Eject reach the generic seam directly, since they are dispatched by the STUNNED actor
 * itself.
 *
 * **`act_mount` deliberately gets NO exception here — and adding one would be an active footgun,
 * not a no-op.** `refereeIdentityAction.ts`'s `request_mount` gate
 * DOES ring `checkActorStatusLegality` — a Mount intent reaches this wall like any other
 * gated verb — but it passes `actionId: null`, and it gates on `intent.actor`, the DISPATCHING
 * ON-FOOT PILOT climbing INTO a mech, never the mech being climbed into. That's what makes RAW's
 * printed grant (p.78: "Pilots can still MOUNT... and can take actions normally") hold **by
 * construction, with no exception list entry needed**: the mech's own Stunned/Shut-Down status
 * never reaches this check at all, because the mech isn't the actor being gated. What the wall
 * DOES still correctly block is a **Down-and-Out pilot** (RAW p.77: unconscious and Stunned) —
 * the pilot IS the gated actor, and an incapacitated pilot genuinely cannot act, Mount included.
 * Do not "helpfully" add `act_mount` to this set: unlike `act_boot_up`/`act_dismount`/`act_eject`
 * (each excepts the SAME Stunned actor the wall is currently blocking, so they can act despite
 * their own status), an `act_mount` entry here would except the DISPATCHING PILOT's own can't-act
 * check — which would let a Down-and-Out pilot Mount, directly regressing the Down-and-Out
 * pin in `encounterRoom.test.ts` (its Mount-verb rejection test). The set exists
 * for "this actor is Stunned/Down-and-Out but this ONE action stays legal for THEM anyway"; Mount
 * doesn't need — and must never get — an entry, because the actor it gates was never the
 * Stunned party RAW is talking about.
 */
const STUNNED_ACTION_EXCEPTIONS = new Set(["act_boot_up", "act_dismount", "act_eject"]);

/**
 * A mech in a Black Thumb Rodeo idles ("the mech can't be controlled from outside, so it will
 * idle"): its only actions are Black Thumb's own, the rider's repairs and End Rodeo, whose ledger
 * ids are the talent's named clauses (`t_black_thumb:2#Field Repair`).
 */
const RODEO_ACTION_PREFIX = "t_black_thumb:";

/** The actor-side status-legality inputs for a standard (non-reaction) verb. */
export type CheckActorStatusLegalityArgs = {
  state: EncounterState;
  /** The combatant attempting the verb. */
  actor: string;
  /**
   * This verb IS a weapon/tech attack (a standalone shot or one Barrage shot) — Jammed's
   * legal-attack-set gate (Improvised Attack/Grapple/Ram survive) only ever applies here.
   */
  isAttack?: boolean;
  /** The attack's weapon id (e.g. `"act_ram"`) when `isAttack` — checked against Jammed's legal set. */
  weapon?: string;
  /** The attack's weapon can be used while Jammed (its entry's `usableWhileJammed`: the Tiger-Hunter Combat Sheathe). */
  weaponUsableWhileJammed?: boolean;
  /**
   * This verb is specifically a TECH action (an Invade tech attack, Scan/Lock On/Bolster, or a
   * Full Tech bundle) — Jammed forbids ALL tech actions outright, attack or not.
   */
  tech?: boolean;
  /**
   * The once-per-turn RAW action id this intent derives (`actionIdForIntent`) — carried ONLY so
   * Stunned's pinned Boot-Up exception can except it (see {@link STUNNED_ACTION_EXCEPTIONS}).
   */
  actionId?: string | null;
};

/**
 * May `actor` legally take a standard (non-reaction) status-gated verb? Checks, in RAW-priority
 * order:
 *   1. **Stunned / Down and Out** (`isStunned()` — Stunned ∨ Shut Down, per Shut Down's composite
 *      status — OR the literal `"Down and Out"` name, RAW p.77's "unconscious and Stunned"): no
 *      actions or Overcharge, full stop, EXCEPT a pinned action id.
 *   2. **Jammed** (RAW p.78): no tech actions at all; weapon/attack verbs limited to Improvised
 *      Attack / Grapple / Ram.
 *   3. **Hack/Slash** ({@link HACK_SLASH_STATUS}): no tech actions.
 * Returns a verdict the room (or the domain reducer's `boost` case) surfaces (a rejection is a
 * logged no-op before any dice). An unknown actor is treated as unblocked (`ok`) — the caller's
 * own existence checks catch a missing combatant with a more specific message.
 */
export function checkActorStatusLegality(args: CheckActorStatusLegalityArgs): StatusLegalityVerdict {
  const { state, actor: actorId, isAttack = false, weapon, weaponUsableWhileJammed = false, tech = false, actionId } = args;
  const actor = state.combatants[actorId];
  if (!actor) return ok(undefined);

  const cantAct = isStunned(actor) || hasStatus(actor.statuses, DOWN_AND_OUT_STATUS);
  if (cantAct && !(actionId != null && STUNNED_ACTION_EXCEPTIONS.has(actionId))) {
    return err(`${actor.name} is Stunned (or Shut Down/Down and Out) — no actions or Overcharge until it clears (RAW p.78)`);
  }

  if (hasStatus(actor.statuses, RODEO_STATUS) && !actionId?.startsWith(RODEO_ACTION_PREFIX)) {
    return err(`${actor.name} is idling in a Black Thumb Rodeo — only Extinguish, Field Repair, Rig Vents or End Rodeo (Long Rim)`);
  }

  if (hasStatus(actor.statuses, JAMMED_STATUS)) {
    if (tech) {
      return err(`${actor.name} is Jammed — can't take tech actions (RAW p.78)`);
    }
    if (isAttack && !weaponUsableWhileJammed && !JAMMED_LEGAL_ATTACK_WEAPONS.has(weapon ?? "")) {
      return err(`${actor.name} is Jammed — attacks are limited to Improvised Attack, Grapple, or Ram (RAW p.78)`);
    }
  }

  if (tech && hasStatus(actor.statuses, HACK_SLASH_STATUS)) {
    return err(`${actor.name} is Hack/Slashed — can't take tech actions until it reboots or shuts down`);
  }

  return ok(undefined);
}

/**
 * May `actorId` take a REACTION at all, status-wise? Jammed and Grappled forbid ALL reactions
 * outright (RAW p.78, plus the house ruling that a Grappled unit can't react);
 * Stunned/Down-and-Out's "no actions or reactions" (RAW p.78) reaches the same conclusion via
 * {@link isStunned}. **Braced** forbids them too (RAW p.73: "you cannot take reactions until the
 * end of your next turn") — the `Braced` status expires at exactly that edge (end of the bracer's
 * next turn), so its presence IS the lockout window. This bites on the CURRENT turn as well as the
 * next: the bracer's reaction slot reopens at every turn start, so without this gate a mech that
 * Braced on an enemy's turn could Overwatch on the very next turn. A live `no_reactions` area
 * holding the actor that it did not make forbids them as well (Wandering Nightmare). The ONE reaction-status gate, consumed by BOTH the room's Overwatch
 * declaration (`request_attack { reaction: true }`) and `reactionRegistry.ts`'s
 * `reactionAvailable` (Brace + any authored/protector reaction) — so Brace, Overwatch, and
 * content reactions all read the identical wall, never a second mechanism. Returns a verdict
 * (never throws); an unknown actor is unblocked (`ok`) — the caller's own existence check catches
 * a missing combatant.
 */
export function checkReactionStatusLegality(state: EncounterState, actorId: string): StatusLegalityVerdict {
  const actor = state.combatants[actorId];
  if (!actor) return ok(undefined);
  if (isStunned(actor) || hasStatus(actor.statuses, DOWN_AND_OUT_STATUS)) {
    return err(`${actor.name} is Stunned (or Shut Down/Down and Out) — no reactions until it clears (RAW p.78)`);
  }
  if (hasStatus(actor.statuses, JAMMED_STATUS)) {
    return err(`${actor.name} is Jammed — no reactions (RAW p.78)`);
  }
  if (hasStatus(actor.statuses, GRAPPLED_STATUS)) {
    return err(`${actor.name} is Grappled — no reactions (decisions/0054)`);
  }
  if (hasStatus(actor.statuses, RODEO_STATUS)) {
    return err(`${actor.name} is idling in a Black Thumb Rodeo — no reactions (Long Rim)`);
  }
  if (hasStatus(actor.statuses, BRACED_STATUS)) {
    return err(`${actor.name} is Braced — no reactions until the end of its next turn (RAW p.73)`);
  }
  // A `no_reactions` area (Wandering Nightmare) holding the actor, unless the actor made it.
  if (actor.pos != null && state.areaEffects !== undefined) {
    const world = worldFor(state);
    const zone = noReactionsAreaHolding(world, state.areaEffects, { id: actor.id, cells: world.footprint(actor.pos, actor.base.size).cells() });
    if (zone !== undefined) return err(`${actor.name} is inside ${state.combatants[zone.source]?.name ?? zone.source}'s distorted timeflow — no reactions`);
  }
  if (hasStatus(actor.statuses, TOWING_STATUS)) {
    return err(`${actor.name} is towing — no reactions while dragging (Core p.63, Lifting and Dragging)`);
  }
  if (hasStatus(actor.statuses, REELING_STATUS)) {
    return err(`${actor.name} is Reeling from an ambush — no reactions until the end of its next turn (Infiltrator)`);
  }
  return ok(undefined);
}

/**
 * May `targetId` legally RECEIVE a beneficial tech action (Bolster) while Jammed or Hack/Slashed? RAW p.78:
 * Jammed blocks "tech actions… including any from allied
 * characters" — the actor-side wall (`checkActorStatusLegality`'s `tech` branch) covers a Jammed
 * actor CASTING tech; this is the sibling target-side read — a Jammed TARGET can't benefit from
 * someone else's tech either. Mirrors the Immunity gate's shape (a target-side
 * pre-dice reject beside the room's tech-immunity gate) but keys on Jammed, not the Immunity
 * facet — the two are independent walls that can both apply to the same target. Returns a
 * verdict the room surfaces; an unknown target is unblocked (`ok`).
 */
export function checkJammedBenefitLegality(state: EncounterState, targetId: string): StatusLegalityVerdict {
  const target = state.combatants[targetId];
  if (!target) return ok(undefined);
  if (hasStatus(target.statuses, JAMMED_STATUS)) {
    return err(`${target.name} is Jammed — can't benefit from tech actions, even from allies (RAW p.78)`);
  }
  if (hasStatus(target.statuses, HACK_SLASH_STATUS)) {
    return err(`${target.name} is Hack/Slashed — can't benefit from tech actions until it reboots or shuts down`);
  }
  return ok(undefined);
}
